import { ENDPOINTS, skapi } from '@/main';
import { FetchOptions } from 'skapi-js';

const ANTHROPIC_MESSAGES_API_URL = 'https://api.anthropic.com/v1/messages';
const ANTHROPIC_MODELS_API_URL = 'https://api.anthropic.com/v1/models';
const ANTHROPIC_VERSION = '2023-06-01';
const ANTHROPIC_MCP_BETA = 'mcp-client-2025-11-20';
const ANTHROPIC_WEB_FETCH_BETA = 'web-fetch-2025-09-10';
const ANTHROPIC_PROMPT_CACHING_BETA = 'prompt-caching-2024-07-31';
const ANTHROPIC_BETA_HEADER = `${ANTHROPIC_MCP_BETA},${ANTHROPIC_WEB_FETCH_BETA},${ANTHROPIC_PROMPT_CACHING_BETA}`;
const WEB_FETCH_MAX_USES = 40; // Max uses of the web_fetch tool per conversation. Adjust based on your needs and model limits.
const WEB_FETCH_MAX_CONTENT_TOKENS = 200000; // Max tokens to fetch from a web page. Adjust based on your needs and model limits.
// const WEB_FETCH_ALLOWED_DOMAINS = [
// 	'https://host-storage-eu-west-1-136564153953-eu71-host.s3.amazonaws.com',
// ];

// ENDPOINTS.then(({ record_public_endpoint }) => {
// 	WEB_FETCH_ALLOWED_DOMAINS.push(record_public_endpoint);
// });

const OPENAI_RESPONSES_API_URL = 'https://api.openai.com/v1/responses';
const OPENAI_MODELS_API_URL = 'https://api.openai.com/v1/models';
const MAX_TOKENS = 25000; // Response max tokens for both Claude and OpenAI models. Adjust as needed based on your use case and model limits.
const DEFAULT_OPENAI_IMAGE_DETAIL = 'auto';
const OPENAI_WEB_SEARCH_ENABLED = true;
const OPENAI_WEB_SEARCH_EXTERNAL_WEB_ACCESS = true;
import { MCP_BASE_URL } from './mcp_oauth';
const MCP_ENDPOINT_URL = MCP_BASE_URL;
const MCP_NAME = 'BunnyQuery';

export const DEFAULT_CLAUDE_MODEL = 'claude-sonnet-4-6';
export const DEFAULT_OPENAI_MODEL = 'gpt-5.4';

const getOpenAIImageDetail = (model?: string) => {
	const normalized = (model || DEFAULT_OPENAI_MODEL).trim().toLowerCase();
	const match = normalized.match(/^gpt-(\d+)(?:\.(\d+))?$/);
	if (!match) {
		return DEFAULT_OPENAI_IMAGE_DETAIL;
	}

	const major = Number(match[1]);
	const minor = match[2] === undefined ? null : Number(match[2]);

	if (major > 5) {
		return 'original';
	}

	if (major === 5 && minor !== null && minor >= 4) {
		return 'original';
	}

	return DEFAULT_OPENAI_IMAGE_DETAIL;
};

export type ClaudeRole = 'user' | 'assistant';

export type ClaudeMessage = {
	role: ClaudeRole;
	content: string;
};

export type OpenAIMessage = {
	role: ClaudeRole;
	content: string;
};

export type ClaudeMcpToolConfig = {
	enabled?: boolean;
	defer_loading?: boolean;
};

export type ClaudeMcpServerRequest = {
	name: string;
	url: string;
	authorizationToken?: string;
	defaultConfig?: ClaudeMcpToolConfig;
	configs?: Record<string, ClaudeMcpToolConfig>;
};

const IMAGE_URL_REGEX =
	/\bhttps?:\/\/[^\s<>"'()\[\]]+?\.(?:jpg|jpeg|png|gif|webp)(?:\?[^\s<>"'()\[\]]*)?/gi;

function transformContentWithImages(
	content: string,
): string | Array<Record<string, any>> {
	if (typeof content !== 'string' || !content) {
		return content;
	}

	const matches = content.match(IMAGE_URL_REGEX);

	if (!matches || !matches.length) {
		return content;
	}

	const seen = new Set<string>();
	const imageBlocks: Array<Record<string, any>> = [];

	for (const url of matches) {
		if (seen.has(url)) continue;
		seen.add(url);
		imageBlocks.push({
			type: 'image',
			source: { type: 'url', url },
		});
	}

	return [...imageBlocks, { type: 'text', text: content }];
}

function prepareClaudeMessages(messages: ClaudeMessage[]) {
	if (!messages.length) return messages;
	// Only transform the most recent user message. Historical user messages
	// may reference image URLs that are now stale (deleted, moved, expired).
	// Re-attaching image blocks to those would cause Anthropic to re-fetch
	// the dead URL and reject the entire request with a validation error.
	const lastIndex = messages.length - 1;
	const last = messages[lastIndex];
	if (last.role !== 'user') return messages;
	const content = transformContentWithImages(last.content);
	if (content === last.content) return messages;
	const next = messages.slice();
	next[lastIndex] = { role: last.role, content } as unknown as ClaudeMessage;
	return next;
}

function transformContentWithOpenAIImages(
	content: string,
	detail = DEFAULT_OPENAI_IMAGE_DETAIL,
): string | Array<Record<string, any>> {
	if (typeof content !== 'string' || !content) {
		return content;
	}

	const matches = content.match(IMAGE_URL_REGEX);

	if (!matches || !matches.length) {
		return content;
	}

	const seen = new Set<string>();
	const imageBlocks: Array<Record<string, any>> = [];

	for (const url of matches) {
		if (seen.has(url)) continue;
		seen.add(url);
		imageBlocks.push({
			type: 'input_image',
			image_url: url,
			detail,
		});
	}

	return [{ type: 'input_text', text: content }, ...imageBlocks];
}

function prepareOpenAIMessages(
	messages: OpenAIMessage[],
	detail = DEFAULT_OPENAI_IMAGE_DETAIL,
) {
	if (!messages.length) return messages;
	// Same rule as Claude: only the latest user turn should be upgraded to
	// image input blocks. Re-sending older image URLs can break if those URLs
	// have expired or moved since the earlier turn.
	const lastIndex = messages.length - 1;
	const last = messages[lastIndex];
	if (last.role !== 'user') return messages;
	const content = transformContentWithOpenAIImages(last.content, detail);
	if (content === last.content) return messages;
	const next = messages.slice();
	next[lastIndex] = { role: last.role, content } as unknown as OpenAIMessage;
	return next;
}

// Attach a cache_control breakpoint to the last message of the stable history
// prefix (everything except the final user turn). Anthropic caches the prefix
// up to and including that marker, so subsequent turns re-use it at ~10% of
// the input-token billing. Messages are converted to content-block form so
// the marker can be attached to a text block.
function applyHistoryCacheBreakpoint(messages: any[]): any[] {
	if (messages.length < 2) return messages;
	// The final message is always the current user turn - don't cache it.
	const breakpointIndex = messages.length - 2;
	return messages.map((m, i) => {
		if (i !== breakpointIndex) return m;
		const blocks = Array.isArray(m.content)
			? m.content.slice()
			: [{ type: 'text', text: m.content }];
		if (!blocks.length) return m;
		const lastBlockIndex = blocks.length - 1;
		blocks[lastBlockIndex] = {
			...blocks[lastBlockIndex],
			cache_control: { type: 'ephemeral' },
		};
		return { ...m, content: blocks };
	});
}

export type CallClaudeWithMcpParams = {
	prompt: string;
	messages?: ClaudeMessage[];
	service: string;
	owner: string;
	model?: string;
	maxTokens?: number;
	system?: string;
	mcpServer: ClaudeMcpServerRequest;
};
export const POLL_INTERVAL = 1500;
export async function callClaudeWithMcp({
	prompt,
	messages,
	service,
	owner,
	model = DEFAULT_CLAUDE_MODEL,
	maxTokens = 1000,
	system,
	mcpServer,
}: CallClaudeWithMcpParams) {
	const mcpServerDefinition: Record<string, any> = {
		type: 'url',
		name: mcpServer.name,
		url: mcpServer.url,
	};

	if (mcpServer.authorizationToken) {
		mcpServerDefinition.authorization_token = mcpServer.authorizationToken;
	}

	return (skapi.clientSecretRequest as any)({
		clientSecretName: 'claude',
		poll: POLL_INTERVAL,
		queue: service,
		service,
		owner,
		url: ANTHROPIC_MESSAGES_API_URL,
		method: 'POST',
		headers: {
			'content-type': 'application/json',
			'x-api-key': '$CLIENT_SECRET',
			'anthropic-version': ANTHROPIC_VERSION,
			'anthropic-beta': ANTHROPIC_BETA_HEADER,
		},
		data: {
			model,
			max_tokens: maxTokens,
			// Send system as a cache-controlled block so Anthropic caches it
			// across turns (billed at ~10% on cache hits). Empty/undefined
			// systems skip the key entirely.
			...(system
				? {
						system: [
							{
								type: 'text',
								text: system,
								cache_control: { type: 'ephemeral' },
							},
						],
					}
				: {}),
			messages: (() => {
				const prepared =
					messages && messages.length
						? prepareClaudeMessages(messages)
						: [
								{
									role: 'user',
									content: transformContentWithImages(prompt),
								},
							];
				// Cache the stable history prefix (everything except the
				// final user turn) so follow-up requests re-use it.
				return applyHistoryCacheBreakpoint(prepared as any[]);
			})(),
			mcp_servers: [mcpServerDefinition],
			tools: [
				{
					type: 'mcp_toolset',
					mcp_server_name: mcpServer.name,
					...(mcpServer.defaultConfig
						? { default_config: mcpServer.defaultConfig }
						: {}),
					...(mcpServer.configs
						? { configs: mcpServer.configs }
						: {}),
				},
				{
					type: 'web_fetch_20250910',
					name: 'web_fetch',
					max_uses: WEB_FETCH_MAX_USES,
					citations: { enabled: true },
					max_content_tokens: WEB_FETCH_MAX_CONTENT_TOKENS,
					// allowed_domains: WEB_FETCH_ALLOWED_DOMAINS,
				},
			],
		},
	});
}

export async function callClaudeWithPublicMcp(
	prompt: string,
	service: string,
	owner: string,
	messages?: ClaudeMessage[],
	system?: string,
	model?: string,
) {
	return callClaudeWithMcp({
		prompt,
		messages,
		service,
		owner,
		model: model || DEFAULT_CLAUDE_MODEL,
		maxTokens: MAX_TOKENS,
		system,
		mcpServer: {
			name: MCP_NAME,
			url: MCP_ENDPOINT_URL,
			authorizationToken: '$ACCESS_TOKEN',
		},
	});
}

export async function callOpenAIWithPublicMcp(
	prompt: string,
	service: string,
	owner: string,
	messages?: OpenAIMessage[],
	system?: string,
	model?: string,
) {
	const resolvedModel = model || DEFAULT_OPENAI_MODEL;
	const imageDetail = getOpenAIImageDetail(resolvedModel);
	const messageList =
		messages && messages.length
			? prepareOpenAIMessages(messages, imageDetail)
			: [
				{
					role: 'user' as const,
					content: transformContentWithOpenAIImages(prompt, imageDetail),
				},
			];

	const responseInput = [
		...(system
			? [
				{
					role: 'system',
					content: system,
				},
			]
			: []),
		...messageList.map((m) => ({
			role: m.role,
			content: m.content,
		})),
	];

	return (skapi.clientSecretRequest as any)({
		clientSecretName: 'openai',
		poll: POLL_INTERVAL,
		queue: service,
		service,
		owner,
		url: OPENAI_RESPONSES_API_URL,
		method: 'POST',
		headers: {
			'content-type': 'application/json',
			Authorization: 'Bearer $CLIENT_SECRET',
		},
		data: {
			model: resolvedModel,
			max_output_tokens: MAX_TOKENS,
			input: responseInput,
			tools: [
				{
					type: 'mcp',
					server_label: MCP_NAME,
					server_url: MCP_ENDPOINT_URL,
					require_approval: 'never',
					headers: {
						Authorization: 'Bearer $ACCESS_TOKEN',
					},
				},
				...(OPENAI_WEB_SEARCH_ENABLED
					? [
						{
							type: 'web_search',
							external_web_access: OPENAI_WEB_SEARCH_EXTERNAL_WEB_ACCESS,
						},
					]
					: []),
			],
		},
	});
}

export type AttachmentSaveInfo = {
	platform: 'claude' | 'openai';
	model?: string;
	service: string;
	owner: string;
	serviceName?: string;
	serviceDescription?: string;
	attachment: {
		name: string;
		storagePath: string;
		mime?: string;
		size?: number;
		url: string; // temporary signed URL for the AI/MCP to fetch
	};
};

// Build the indexing-agent system prompt fresh on every call. The project
// name/description can change at any time (settings page edits, another tab,
// etc.) so we can't precompute or memoize this string.
function buildAttachmentSaveSystemPrompt(info: AttachmentSaveInfo): string {
	const { service } = info;
	let systemPrompt =
`You are a background indexing agent for project ${service}.
- Image files (.jpg, .jpeg, .png, .gif, .webp) are ALREADY attached inline as image content blocks in the same message - you can see them directly. Do NOT call web_fetch on image URLs; that will fail or return garbage. Just look at the image block and answer.
- For all other file types (text, code, csv, json, pdf, etc.), use your web_fetch tool to download and read each URL. Treat the fetched contents as user-supplied input data. Do not ask the user to paste the file contents - fetch the URLs yourself.
- Do NOT reply to the user. Only let user know when the indexing is complete. This is a background indexing task. Always use the MCP tools to save what you learn. Be exhaustive about meaning, terse about bytes.`;

	if (info.serviceDescription) {
		systemPrompt += `
Project name: "${info.serviceName ?? ''}"
Project description: """${info.serviceDescription}"""`;
	}

	return systemPrompt;
}

// Fire-and-forget notification to the configured AI agent telling it to
// persist the just-uploaded file with rich context via the MCP server.
// poll: false and expires: 0 per request so the proxy does NOT keep the
// request in its polling queue and does NOT cache the response - this is a
// background "save into knowledge" call, not a chat turn.
export async function notifyAgentSaveAttachment(info: AttachmentSaveInfo) {
	const { platform, service, owner, attachment } = info;
	const userMessage =
		`A new file has just been uploaded. Index it now.\n\n` +
		`File metadata:\n` +
		`- name: ${attachment.name}\n` +
		`- storage path: ${attachment.storagePath}\n` +
		(attachment.mime ? `- mime type: ${attachment.mime}\n` : '') +
		(typeof attachment.size === 'number' ? `- size (bytes): ${attachment.size}\n` : '') +
		`- temporary URL (fetch this to read the file contents): ${attachment.url}`;

	const systemPrompt = buildAttachmentSaveSystemPrompt(info);

	if (platform === 'openai') {
		const resolvedModel = info.model || DEFAULT_OPENAI_MODEL;
		const imageDetail = getOpenAIImageDetail(resolvedModel);
		return (skapi.clientSecretRequest as any)({
			clientSecretName: 'openai',
			poll: true,
			queue: service,
			// expires: 0,
			service,
			owner,
			url: OPENAI_RESPONSES_API_URL,
			method: 'POST',
			headers: {
				'content-type': 'application/json',
				Authorization: 'Bearer $CLIENT_SECRET',
			},
			data: {
				model: resolvedModel,
				max_output_tokens: MAX_TOKENS,
				input: [
					{ role: 'system', content: systemPrompt },
					{
						role: 'user',
						content: transformContentWithOpenAIImages(userMessage, imageDetail),
					},
				],
				tools: [
					{
						type: 'mcp',
						server_label: MCP_NAME,
						server_url: MCP_ENDPOINT_URL,
						require_approval: 'never',
						headers: { Authorization: 'Bearer $ACCESS_TOKEN' },
					},
					...(OPENAI_WEB_SEARCH_ENABLED
						? [
								{
									type: 'web_search',
									external_web_access: OPENAI_WEB_SEARCH_EXTERNAL_WEB_ACCESS,
								},
							]
						: []),
				],
			},
		});
	}

	const resolvedModel = info.model || DEFAULT_CLAUDE_MODEL;
	return (skapi.clientSecretRequest as any)({
		clientSecretName: 'claude',
		poll: true,
		queue: service,
		// expires: 0,
		service,
		owner,
		url: ANTHROPIC_MESSAGES_API_URL,
		method: 'POST',
		headers: {
			'content-type': 'application/json',
			'x-api-key': '$CLIENT_SECRET',
			'anthropic-version': ANTHROPIC_VERSION,
			'anthropic-beta': ANTHROPIC_BETA_HEADER,
		},
		data: {
			model: resolvedModel,
			max_tokens: MAX_TOKENS,
			system: [
				{
					type: 'text',
					text: systemPrompt,
					cache_control: { type: 'ephemeral' },
				},
			],
			messages: [
				{
					role: 'user',
					content: transformContentWithImages(userMessage),
				},
			],
			mcp_servers: [
				{
					type: 'url',
					name: MCP_NAME,
					url: MCP_ENDPOINT_URL,
					authorization_token: '$ACCESS_TOKEN',
				},
			],
			tools: [
				{
					type: 'mcp_toolset',
					mcp_server_name: MCP_NAME,
				},
				{
					type: 'web_fetch_20250910',
					name: 'web_fetch',
					max_uses: WEB_FETCH_MAX_USES,
					citations: { enabled: true },
					max_content_tokens: WEB_FETCH_MAX_CONTENT_TOKENS,
				},
			],
		},
	});
}

export function extractClaudeText(response: any) {
	if (!Array.isArray(response?.content)) {
		return '';
	}

	return response.content
		.filter((block: any) => block?.type === 'text')
		.map((block: any) => block.text)
		.join('\n');
}

export function extractOpenAIText(response: any) {
	if (
		typeof response?.output_text === 'string' &&
		response.output_text.length
	) {
		return response.output_text;
	}

	if (Array.isArray(response?.output)) {
		const text = response.output
			.flatMap((item: any) => item?.content || [])
			.filter((part: any) => part?.type === 'output_text')
			.map((part: any) => part.text || '')
			.join('\n')
			.trim();

		if (text) {
			return text;
		}
	}

	const content = response?.choices?.[0]?.message?.content;

	if (typeof content === 'string') {
		return content;
	}

	if (Array.isArray(content)) {
		return content
			.map((part: any) => {
				if (typeof part === 'string') {
					return part;
				}
				if (part?.type === 'text') {
					return part.text || '';
				}
				return '';
			})
			.join('\n');
	}

	return '';
}

export async function listClaudeModels(service: string, owner: string) {
	return (skapi.clientSecretRequest as any)({
		clientSecretName: 'claude',
		service,
		owner,
		url: ANTHROPIC_MODELS_API_URL,
		method: 'GET',
		headers: {
			'x-api-key': '$CLIENT_SECRET',
			'anthropic-version': ANTHROPIC_VERSION,
		},
	});
}

export async function listOpenAIModels(service: string, owner: string) {
	return (skapi.clientSecretRequest as any)({
		clientSecretName: 'openai',
		service,
		owner,
		url: OPENAI_MODELS_API_URL,
		method: 'GET',
		headers: {
			Authorization: 'Bearer $CLIENT_SECRET',
		},
	});
}

export async function getChatHistory(
	params: { service?: string; owner?: string; platform: 'claude' | 'openai'; poll?: number },
	fetchOptions: FetchOptions,
) {
	let url =
		params.platform === 'claude'
			? ANTHROPIC_MESSAGES_API_URL
			: OPENAI_RESPONSES_API_URL;
	let p = Object.assign(
		{
			url,
			method: 'POST',
		},
		{ service: params.service, owner: params.owner, poll: params.poll, queue: params.service },
	);

	return skapi.clientSecretRequestHistory(
		p as { url: string; method: 'POST'; poll?: number; queue?: string; },
		Object.assign({ ascending: false }, fetchOptions),
	);
}
